<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';

import {
  getHotwordDemoGraphicService,
  getHotwordBaseAnalysisService,
  getHordwordSourceAnalysisService,
} from '@/api/dp/index.js';
import HotwordBasicTable from './components/hotword-basic-table.vue';
import HotwordAgeGenderTable from './components/hotword-age-gender-table.vue';
import HotwordSnsTable from './components/hotword-sns-table.vue';

const route = useRoute();
const router = useRouter();

/** クエリから受け取るコンテキスト（index.vue の goToHotwordDetail と対応） */
const movieName = computed(() => String(route.query.movieName ?? ''));
const movieId = computed(() => Number(route.query.movieId));
const dateFrom = computed(() => Number(route.query.dateFrom));
const dateTo = computed(() => Number(route.query.dateTo));

/** 期間ラベル（dateTo は翌日0時・排他のため表示上は -1 日） */
const dateLabel = computed(() => {
  if (!dateFrom.value || !dateTo.value) return '';
  return `${dayjs(dateFrom.value).format('YYYY/MM/DD')} 〜 ${dayjs(dateTo.value - 86400000).format('YYYY/MM/DD')}`;
});

const goBack = () => {
  if (window.history.length > 1) {
    router.back();
    return;
  }
  router.push('/client/client-v2');
};

// ─── Toolbar state（キーワード別 基本分析テーブル専用） ─────────────────
const searchKeyword = ref('');

/** 温度フィルタ：全て / ULTRA HOT / HOT / HIGH / MID / LOW
 *  レビュー・感情分析（index.vue / hotword.vue）のヒートスケールと同期 */
const tempFilters = [
  { value: 'all', label: '全て' },
  { value: 'ultra', label: 'ULTRA HOT' },
  { value: 'hot', label: 'HOT' },
  { value: 'high', label: 'HIGH' },
  { value: 'mid', label: 'MID' },
  { value: 'low', label: 'LOW' },
] as const;
const selectedTemp = ref<string>('all');

/** 並び順：熱量スコア（既定）/ 前週比（非表示）/ 投稿数 */
const sortFilters = [
  { value: 'heat', label: '熱量スコア', hidden: false },
  { value: 'wow', label: '前週比', hidden: true },
  { value: 'post', label: '投稿数', hidden: false },
];
const selectedSort = ref<string>('heat');

// ─── キーワード共通の温度スケール ───────────────
type Temperature = 'ultra' | 'hot' | 'high' | 'mid' | 'low';

/**
 * レビュー・感情分析（hotword.vue の getSize / index.vue の HOTWORD_SIZE_LABELS）と同一ロジック。
 * キーワードのランキング順位（0 始まり）→ サイズ 5..1 → 温度。API 応答順をランクとして扱う。
 *   size 5: ULTRA HOT（rank 0）
 *   size 4: HOT（rank 1-2）
 *   size 3: HIGH（rank 3-6）
 *   size 2: MID（rank 7-11）
 *   size 1: LOW（rank 12+）
 */
const getHotwordSize = (index: number): number => {
  if (index < 1) return 5;
  if (index < 3) return 4;
  if (index < 7) return 3;
  if (index < 12) return 2;
  return 1;
};
const TEMP_BY_SIZE: Record<number, Temperature> = {
  5: 'ultra',
  4: 'hot',
  3: 'high',
  2: 'mid',
  1: 'low',
};
/** ランキング順位（0 始まり）から温度を導出。レビュー・感情分析のヒートスケールと同期。 */
const tempByRank = (index: number): Temperature => TEMP_BY_SIZE[getHotwordSize(index)] ?? 'low';

// ─── キーワード別 基本分析（Figma: 先頭フル幅パネル） ───────────────
// TODO: 現状は Figma のモックデータ。API 整備後に getHotwordBasicAnalysisService(...) 等で差し替え。

interface BasicRow {
  keyword: string;
  /** ドット色（AGE_DOT_PALETTE を index で巡回） */
  dotColor: string;
  /** 温度（API 応答順のランクから tempByRank で導出。レビュー・感情分析と同期） */
  temp: Temperature;
  /** 元のランキング順位（API 応答順。熱量スコア順ソートに使用） */
  heatRank: number;
  /** 投稿数（API の mentionCount） */
  mentionCount: number;
  /** 主年齢（API の mainAgeGroup） */
  mainAgeGroup: string;
}

/** 温度別ヒートバッジのメタ（label 表示用）。
 *  bg/border/color は light 用フォールバック。実際の描画は hotword-basic-table の
 *  `.hd-heat-badge--*` + `html.dark` オーバーライド（半透明アクセント）を使う。 */
const basicTempMeta: Record<Temperature, { label: string; bg: string; border: string; color: string }> = {
  ultra: { label: 'ULTRA HOT', bg: 'rgba(220, 38, 38, 0.15)', border: '#FCA5A5', color: '#DC2626' },
  hot:   { label: 'HOT',       bg: 'rgba(236, 72, 153, 0.15)', border: '#F9A8D4', color: '#EC4899' },
  high:  { label: 'HIGH',      bg: 'rgba(234, 88, 12, 0.15)', border: '#FDBA74', color: '#EA580C' },
  mid:   { label: 'MID',       bg: 'rgba(22, 163, 74, 0.15)', border: '#86EFAC', color: '#16A34A' },
  low:   { label: 'LOW',       bg: 'rgba(107, 114, 128, 0.15)', border: '#CBD5E1', color: '#6B7280' },
};

const basicRows = ref<BasicRow[]>([]);

/** 投稿数の compact 表記（52000 → 5.2万、9000 → 9,000） */
const formatPostCount = (n: number): string => {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return n.toLocaleString();
};

/** フィルタ＋ソート後の基本分析行（ツールバーの検索 / 温度 / 並び順に追従） */
const displayedBasicRows = computed<BasicRow[]>(() => {
  const kw = searchKeyword.value.trim();
  const base = basicRows.value.filter((r) => {
    if (selectedTemp.value === 'all') return true;
    return r.temp === selectedTemp.value;
  });
  const searched = kw ? base.filter((r) => r.keyword.includes(kw)) : base;
  const sorted = [...searched];
  if (selectedSort.value === 'heat') {
    // 熱量スコア順：API 応答順（heatRank）が小さいほど熱い
    sorted.sort((a, b) => a.heatRank - b.heatRank);
  } else if (selectedSort.value === 'post') {
    sorted.sort((a, b) => b.mentionCount - a.mentionCount);
  }
  // 'wow' は hidden: true なので選択されない
  return sorted;
});

/** 件数表示（例：15 / 28 件（TOP 15））。基本分析テーブルのみ対象 */
const countText = computed(
  () => `${displayedBasicRows.value.length} / ${basicRows.value.length} 件（TOP ${displayedBasicRows.value.length}）`
);

// ─── 年齢・性別層分析（Figma: フィルタ右のパネル） ───────────────────
// getHotwordDemoGraphicService で取得。API 仕様:
//   ageGroups: ["10代","20代",...]
//   genders:   ["男性","女性"]
//   items: [{ keyword, totalCount, matrix: { [ageGroup]: { 男性: {count,percent}, 女性: {count,percent} } } }]

interface DemoGenderCell { count: number; percent: number }
type DemoMatrix = Record<string, Record<string, DemoGenderCell>>;
interface DemoItem {
  keyword: string;
  totalCount: number;
  maleTotalPercent: number;
  femaleTotalPercent: number;
  matrix: DemoMatrix;
}
interface DemoGraphicResponse {
  ageGroups: string[];
  genders: string[];
  items: DemoItem[];
}

/** 年代グループ（API の ageGroups をそのまま反映） */
const ageGroups = ref<string[]>([]);

/** 行内セル：male/female は API の percent を格納（バー長・表示とも percent 使用） */
type AgeCell = { male: number; female: number };
interface AgeRowGendered {
  keyword: string;
  dotColor: string;
  totalCount: number;
  /** 全世代男性構成比（API の maleTotalPercent） */
  maleTotalPercent: number;
  /** 全世代女性構成比（API の femaleTotalPercent） */
  femaleTotalPercent: number;
  /** 温度（API 応答順のランクから tempByRank で導出。レビュー・感情分析と同期） */
  temp: Temperature;
  cells: AgeCell[]; // ageGroups と同長
}

/** キーワードのドット色。API は色を返さないため、出現順にパレットから割り当てる */
const AGE_DOT_PALETTE = [
  '#DC2626', '#EA580C', '#D97706', '#CA8A04', '#16A34A', '#0891B2',
  '#7C3AED', '#BE185D', '#0369A1', '#1D4ED8', '#B91C1C', '#6D28D9',
  '#0F766E', '#475569',
];
const pickDotColor = (i: number) => AGE_DOT_PALETTE[i % AGE_DOT_PALETTE.length];

const ageRows = ref<AgeRowGendered[]>([]);
const ageLoading = ref(false);

/** レスポンスのラップ（res 直 / res.data）を吸収して DemoGraphicResponse を取り出す */
const pickDemoGraphic = (res: any): DemoGraphicResponse | null => {
  const obj = res && typeof res === 'object'
    ? ((res.data && typeof res.data === 'object' && !Array.isArray(res.data)) ? res.data : res)
    : null;
  if (!obj || !Array.isArray((obj as any).items)) return null;
  return obj as DemoGraphicResponse;
};

/** 年齢・性別層分析データを取得し、テーブル行へ変換して格納 */
const fetchAgeGender = () => {
  if (!movieId.value) return;
  ageLoading.value = true;
  getHotwordDemoGraphicService({
    movieId: movieId.value,
    ...(dateFrom.value ? { dateFrom: dateFrom.value } : {}),
    ...(dateTo.value ? { dateTo: dateTo.value } : {}),
    ...(route.query.word ? { word: String(route.query.word) } : {}),
  })
    .then((res: any) => {
      const data = pickDemoGraphic(res);
      if (!data) {
        ageGroups.value = [];
        ageRows.value = [];
        return;
      }
      const groups = Array.isArray(data.ageGroups) ? data.ageGroups : [];
      ageGroups.value = groups;
      ageRows.value = (Array.isArray(data.items) ? data.items : []).map((it, i) => ({
        keyword: String(it?.keyword ?? ''),
        dotColor: pickDotColor(i),
        totalCount: Number(it?.totalCount ?? 0) || 0,
        maleTotalPercent: Number(it?.maleTotalPercent ?? 0) || 0,
        femaleTotalPercent: Number(it?.femaleTotalPercent ?? 0) || 0,
        temp: tempByRank(i),
        cells: groups.map((g) => {
          const cell = it?.matrix?.[g] || {};
          const male = Number(cell?.男性?.percent ?? 0) || 0;
          const female = Number(cell?.女性?.percent ?? 0) || 0;
          return { male, female };
        }),
      }));
    })
    .catch(() => {
      ageGroups.value = [];
      ageRows.value = [];
    })
    .finally(() => {
      ageLoading.value = false;
    });
};

/** キーワード別 基本分析データを取得し、テーブル行へ変換して格納。
 *  API は keyword / mentionCount / mainAgeGroup を返す。
 *  温度は API が持たないため、応答順のランクから tempByRank で導出（レビュー・感情分析と同期）。 */
const fetchBasicAnalysis = () => {
  if (!movieId.value) return;
  getHotwordBaseAnalysisService({
    movieId: movieId.value,
    ...(dateFrom.value ? { dateFrom: dateFrom.value } : {}),
    ...(dateTo.value ? { dateTo: dateTo.value } : {}),
    ...(route.query.word ? { word: String(route.query.word) } : {}),
  })
    .then((res: any) => {
      const arr = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];
      basicRows.value = arr.map((it: any, i: number) => ({
        keyword: String(it?.keyword ?? ''),
        dotColor: pickDotColor(i),
        temp: tempByRank(i),
        heatRank: i,
        mentionCount: Number(it?.mentionCount ?? 0) || 0,
        mainAgeGroup: String(it?.mainAgeGroup ?? ''),
      }));
    })
    .catch(() => {
      basicRows.value = [];
    });
};

onMounted(() => {
  fetchAgeGender();
  fetchBasicAnalysis();
  fetchSnsSourceAnalysis();
});

/** 行内最大値のセルキー（`${ageIndex}-${gender}` または `all-${gender}`）。is-max ハイライト用 */
const rowMaxKey = (row: AgeRowGendered): string => {
  let max = -1;
  let key = '';
  row.cells.forEach((c, i) => {
    if (c.male > max) {
      max = c.male;
      key = `${i}-male`;
    }
    if (c.female > max) {
      max = c.female;
      key = `${i}-female`;
    }
  });
  if (row.maleTotalPercent > max) {
    max = row.maleTotalPercent;
    key = 'all-male';
  }
  if (row.femaleTotalPercent > max) {
    max = row.femaleTotalPercent;
    key = 'all-female';
  }
  return key;
};

/** ミニバーの塗り幅（%）＝ 表示テキストのパーセント値をそのまま幅にする */
const barWidth = (value: number): string => `${value}%`;

/** ヒート背景のアルファ（Figma 準拠：値が大きいほど濃い赤） */
const cellBg = (value: number): string => {
  const alpha = Math.min(0.55, 0.08 + (value / 100) * 1.25);
  return `rgba(239, 68, 68, ${alpha.toFixed(3)})`;
};

/** ヘッダの LOW→HIGH 凡例バー（Figma の 6 段階アルファ） */
const heatLegendAlphas = [0.043, 0.137, 0.247, 0.357, 0.467, 0.55];

// ─── キーワード別 男女比分析（Figma: 下段パネル） ─────────────
// TODO: 現状は Figma のモックデータ。API 整備後に getHotwordGenderRatioService(...) 等で差し替え。
type GenderTrend = 'male' | 'female' | 'same';

interface GenderRow {
  keyword: string;
  dotColor: string;
  male: number; // 男性の比率（%）
  female: number; // 女性の比率（%）
  trend: GenderTrend; // 多い側 / 同じ
  diff: number; // 差分（pt）
}

const genderRows = ref<GenderRow[]>([
  { keyword: '泣いた', dotColor: '#DC2626', male: 38, female: 62, trend: 'female', diff: 24 },
  { keyword: '感動した', dotColor: '#EA580C', male: 42, female: 58, trend: 'female', diff: 16 },
  { keyword: '名演技', dotColor: '#D97706', male: 53, female: 47, trend: 'same', diff: 6 },
  { keyword: '歴史的名作', dotColor: '#CA8A04', male: 57, female: 43, trend: 'male', diff: 14 },
  { keyword: 'リピート必至', dotColor: '#16A34A', male: 44, female: 56, trend: 'female', diff: 12 },
  { keyword: '圧巻', dotColor: '#0891B2', male: 55, female: 45, trend: 'same', diff: 10 },
  { keyword: '美しい映像', dotColor: '#7C3AED', male: 46, female: 54, trend: 'same', diff: 8 },
  { keyword: '心に刺さる', dotColor: '#BE185D', male: 35, female: 65, trend: 'female', diff: 30 },
  { keyword: '家族で観たい', dotColor: '#0369A1', male: 50, female: 50, trend: 'same', diff: 0 },
  { keyword: '語り継がれる', dotColor: '#1D4ED8', male: 56, female: 44, trend: 'male', diff: 12 },
  { keyword: '涙が止まらない', dotColor: '#B91C1C', male: 34, female: 66, trend: 'female', diff: 32 },
  { keyword: '邦画最高傑作', dotColor: '#6D28D9', male: 60, female: 40, trend: 'male', diff: 20 },
  { keyword: '役者の迫力', dotColor: '#0F766E', male: 58, female: 42, trend: 'male', diff: 16 },
  { keyword: '衝撃展開', dotColor: '#475569', male: 56, female: 44, trend: 'male', diff: 12 },
  { keyword: '映画館で観て', dotColor: '#D97706', male: 56, female: 44, trend: 'male', diff: 12 },
]);

// ─── SNS別 流入元分析（Figma: 年齢・性別層分析の下段パネル） ─────────────
interface SnsRow {
  keyword: string;
  dotColor: string;
  mentionCount: number;
  totalPercent: number;
}

const snsLoading = ref(false);
const snsRows = ref<SnsRow[]>([]);

/** SNS別 各列のメタ（ヘッダのドット色 / バー塗り色 / ラベル）。
 *  color はバー塗りと TOP バッジの背景色にも使用する。 */
const snsColumns = [
  { key: 'mentionCount', label: '投稿数', showBar: false },
  { key: 'totalPercent', label: '合計', dot: 'var(--text-primary)', color: 'var(--text-primary)', showBar: true },
] as const;

const snsCellPercent = (row: SnsRow, key: string): number => {
  if (key === 'totalPercent') return row.totalPercent;
  return 0;
};

/** SNS別 流入元分析データを取得し、テーブル行へ変換して格納。 */
const fetchSnsSourceAnalysis = () => {
  if (!movieId.value) return;
  snsLoading.value = true;
  getHordwordSourceAnalysisService({
    movieId: movieId.value,
  })
    .then((res: any) => {
      const arr = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];
      snsRows.value = arr.map((it: any, i: number) => ({
        keyword: String(it?.keyword ?? ''),
        dotColor: pickDotColor(i),
        mentionCount: Number(it?.mentionCount ?? 0) || 0,
        totalPercent: Number(it?.totalPercent ?? 0) || 0,
      }));
    })
    .catch(() => {
      snsRows.value = [];
    })
    .finally(() => {
      snsLoading.value = false;
    });
};
</script>

<template>
  <div class="hotword-detail-page">
    <!-- Page Header -->
    <div class="hd-header">
      <button class="hd-back-btn" @click="goBack">
        <i class="fas fa-arrow-left"></i>
        <span>戻る</span>
      </button>
      <div class="hd-header-center" v-if="movieName || dateLabel">
        <div v-if="movieName" class="hd-movie-name">{{ movieName }}</div>
        <div v-if="dateLabel" class="hd-date-label">
          <i class="fas fa-calendar-alt"></i>
          {{ dateLabel }}
        </div>
      </div>
    </div>

    <HotwordBasicTable
      :displayed-basic-rows="displayedBasicRows"
      :temp-filters="tempFilters"
      :selected-temp="selectedTemp"
      :sort-filters="sortFilters"
      :selected-sort="selectedSort"
      :search-keyword="searchKeyword"
      :count-text="countText"
      :basic-temp-meta="basicTempMeta"
      :format-post-count="formatPostCount"
      @update:search-keyword="searchKeyword = $event"
      @update:selected-temp="selectedTemp = $event"
      @update:selected-sort="selectedSort = $event"
    />

    <div class="hd-panels-row hd-panels-row--single">
      <HotwordAgeGenderTable
        :age-groups="ageGroups"
        :age-rows="ageRows"
        :age-loading="ageLoading"
        :heat-legend-alphas="heatLegendAlphas"
        :row-max-key="rowMaxKey"
        :cell-bg="cellBg"
        :bar-width="barWidth"
      />
    </div>

    <HotwordSnsTable
      :sns-rows="snsRows"
      :sns-loading="snsLoading"
      :sns-columns="snsColumns"
      :sns-cell-percent="snsCellPercent"
      :format-post-count="formatPostCount"
    />
  </div>
</template>

<style lang="less" scoped src="./hotword-detail.less"></style>
